import { Form } from '@redwoodjs/forms';
import { useState } from 'react';
import { useCreateDocument } from 'src/api/documents.api';
import { useAuth } from 'src/auth';
import FileDropzone from 'src/components/atoms/FileDropzone';
import { AgAutocomplete, AgButton, AgText } from 'src/components/ui';
import { uploadToFilestack } from 'src/lib/filestack';
import { requirePresent } from 'src/lib/requirePresent';
import type { CreateDocument, DocumentType } from 'types/graphql';

const allDocumentTypeOptions: Array<{ label: string; value: DocumentType }> = [
  { label: 'Purchase Order', value: 'PURCHASE_ORDER_CONFIRMATION' },
  { label: 'Receiving Ticket', value: 'RECEIVING_TICKET' },
  { label: 'Payable', value: 'PAYABLE' },
  { label: 'Sales Order', value: 'SALES_ORDER_CONFIRMATION' },
  { label: 'Pick Ticket', value: 'PICK_TICKET' },
  { label: 'Bill of Lading', value: 'BILL_OF_LADING' },
  { label: 'Invoice', value: 'INVOICE' },
  { label: 'Grower Product', value: 'WORK_ORDER_CONFIRMATION' },
  { label: 'Grower Settlement', value: 'GROWER_SETTLEMENT' },
  { label: 'Other', value: 'OTHER' },
];

export const ALL_DOCUMENT_TYPES = allDocumentTypeOptions.map((option) => option.value);

/** The parents a document can hang off — the `createDocument` argument list, minus `input`. */
export type DocumentOwnerId = {
  purchaseOrderId?: number;
  salesOrderId?: number;
  buySellOrderId?: number;
  workOrderId?: number;
  lotId?: number;
  productionRunId?: number;
  billId?: number;
};

/** The `createDocument` selection `onDocumentUploaded` receives. */
export type UploadedDocument = CreateDocument['createDocument'];

export default function DocumentUploadForm({
  documentOwnerId,
  onDocumentUploaded,
  selectableDocumentTypes = ALL_DOCUMENT_TYPES,
}: {
  documentOwnerId: DocumentOwnerId;
  onDocumentUploaded?: (document: UploadedDocument) => void;
  selectableDocumentTypes?: DocumentType[];
}) {
  const [documentType, setDocumentType] = useState<{ label: string; value: DocumentType } | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isFileUploading, setIsFileUploading] = useState(false);

  const { currentUser } = useAuth();
  const { create: createDocument } = useCreateDocument();

  const validateForm = () => {
    if (!documentType) {
      return 'Document type must be selected.';
    }

    if (!selectedFile) {
      return 'A file must be selected for upload.';
    }

    return;
  };

  const resetForm = () => {
    setDocumentType(null);
    setSelectedFile(null);
    setFormError(null);
  };

  const handleSubmit = async () => {
    const formError = validateForm();
    if (formError) {
      setFormError(formError);
      return;
    }
    // Unreachable after validateForm(); narrows both for the upload below.
    if (!documentType || !selectedFile) return;

    setIsFileUploading(true);
    const uploadResult = await uploadToFilestack(selectedFile);
    const result = await createDocument({
      ...documentOwnerId,
      input: {
        title: uploadResult._file.name,
        url: uploadResult.url,
        type: documentType.value,
        uploadedById: requirePresent(currentUser, 'membershipId').membershipId,
      },
    });

    onDocumentUploaded && onDocumentUploaded(requirePresent(result.data, 'createDocument').createDocument);

    setIsFileUploading(false);
    resetForm();
  };

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <AgText size="16" className="font-semibold mb-4">
        Upload New Document
      </AgText>
      {formError && (
        <AgText size="13" className="mt-1 text-red-text">
          {formError}
        </AgText>
      )}

      <div className="flex flex-col gap-2 my-2">
        {/* Document type */}
        <AgAutocomplete<{ label: string; value: DocumentType }>
          label="Document Type"
          size="sm"
          isRequired
          value={documentType}
          onChange={(value) => setDocumentType(value)}
          getOptionLabel={(option) => option.label}
          options={allDocumentTypeOptions.filter((option) => selectableDocumentTypes.includes(option.value))}
        />

        {/* Drag and drop file uploader */}
        <FileDropzone
          selectedFile={selectedFile}
          onFileAccepted={setSelectedFile}
          onFileRejected={setFormError}
          handleClearFile={() => setSelectedFile(null)}
        />

        {/* Action buttons */}
        <div className="ml-auto mt-2 flex gap-2">
          <AgButton isDisabled={isFileUploading} onPress={resetForm} variant="destructive-white">
            Reset
          </AgButton>
          <div>
            <AgButton type="submit" variant="primary" isDisabled={isFileUploading}>
              Upload
            </AgButton>
            {isFileUploading && (
              <div className="mt-1 h-1 w-full overflow-hidden rounded bg-gray-200">
                <div className="h-full animate-pulse bg-current rounded" />
              </div>
            )}
          </div>
        </div>
      </div>
    </Form>
  );
}
